import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, FolderTree, ChevronRight, ChevronDown } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';  
 
const AccountGroups = () => {

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      // Handle both direct sessionData and nested sessionData.session (same as DashboardHome)
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS MUST COME BEFORE ANY CONDITIONAL RETURNS
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  
  const [formData, setFormData] = useState({
    acgroupid: null,
    companyid: companyId || null,
    acgroupcode: '',
    acgroupname: '',
    actype: 'Asset',
    acclass: '',
    parentid: '',
    grouporder: 0,
    statushide: false
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Account Types
  const accountTypes = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];
  
  // Account Classes by Type
  const accountClasses = {
    'Asset': ['Current Asset', 'Fixed Asset', 'Intangible Asset', 'Other Asset'],
    'Liability': ['Current Liability', 'Long Term Liability'],
    'Equity': ['Capital', 'Retained Earnings', 'Drawings'],
    'Income': ['Operating Income', 'Non-Operating Income', 'Other Income'],
    'Expense': ['Cost of Sales', 'Operating Expense', 'Administrative Expense', 'Financial Expense']
  };

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]); 

  useEffect(() => {
    filterGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterType, groups]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/Acgroups.php', {
        params: {
          companyid: companyId,
          statushide: 0
        }
      });
      
      const data = response.data;  // ✅ FIXED: Changed from response.json()
      
      if (data.success) {
        setGroups(data.data || []);
        buildTreeData(data.data || []);
      } else {
        if (data.message.includes('Session context required')) {
          alert('Please select a company and financial year first.\n\nGo to: Profile → Select Company → Select Year');
        } else {
          alert('Failed to fetch account groups: ' + data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching account groups:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error connecting to backend...');
      }
    } finally {
      setLoading(false);  // ✅ FIXED: Added finally block
    }
  };

  const buildTreeData = (flatData) => {
    const tree = [];
    const map = {};
    
    // Create a map of all items
    flatData.forEach(item => {
      map[item.acgroupid] = { ...item, children: [] };
    });
    
    // Build the tree
    flatData.forEach(item => {
      if (item.parentid === null || item.parentid === undefined) {
        tree.push(map[item.acgroupid]);
      } else if (map[item.parentid]) {
        map[item.parentid].children.push(map[item.acgroupid]);
      }
    });
    
    setTreeData(tree);
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderTree = (nodes, level = 0) => {
    return nodes.map(node => (
      <div key={node.acgroupid} style={{ marginLeft: `${level * 20}px` }}>
        <div 
          className="tree-node"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '4px',
            marginBottom: '4px',
            background: node.statushide ? '#fee2e2' : '#f9fafb',
            border: '1px solid #e5e7eb'
          }}
        >
          <span onClick={() => node.children?.length > 0 && toggleNode(node.acgroupid)}>
            {node.children?.length > 0 ? (
              expandedNodes.has(node.acgroupid) ? 
                <ChevronDown size={16} /> : 
                <ChevronRight size={16} />
            ) : <span style={{ width: '16px', display: 'inline-block' }}></span>}
          </span>
          <FolderTree size={16} style={{ marginLeft: '8px', color: '#667eea' }} />
          <span style={{ flex: 1, marginLeft: '8px', fontWeight: '600' }}>{node.acgroupname}</span>
          <span className="type-badge" style={{
            background: getTypeColor(node.actype),
            color: '#fff',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            marginRight: '8px'
          }}>
            {node.actype}
          </span>
          <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '8px' }}>
            {node.childcount || 0} sub-groups, {node.headcount || 0} heads
          </span>
          <button
            className="btn-icon btn-edit"
            onClick={(e) => { e.stopPropagation(); handleEdit(node); }}
            title="Edit"
            style={{ marginRight: '4px' }}
          >
            <Edit2 size={14} />
          </button>
          <button
            className="btn-icon btn-delete"
            onClick={(e) => { e.stopPropagation(); handleDelete(node.acgroupid); }}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {expandedNodes.has(node.acgroupid) && node.children?.length > 0 && renderTree(node.children, level + 1)}
      </div>
    ));
  };

  const getTypeColor = (type) => {
    const colors = {
      'Asset': '#10b981',
      'Liability': '#ef4444',
      'Equity': '#3b82f6',
      'Income': '#8b5cf6',
      'Expense': '#f59e0b'
    };
    return colors[type] || '#6b7280';
  };

  const filterGroups = () => {
    let filtered = groups;

    if (filterType !== 'all') {
      filtered = filtered.filter(group => group.actype === filterType);
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter(group =>
        group.acgroupname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.acgroupcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.acclass?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredGroups(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.acgroupname.trim()) {
      newErrors.acgroupname = 'Account group name is required';
    }
    
    if (!formData.actype) {
      newErrors.actype = 'Account type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const method = editMode ? 'put' : 'post';  // ✅ FIXED: Changed to lowercase
      const response = await apiClient[method]('/Acgroups.php', {
        ...formData,
        companyid: companyId,
        statushide: formData.statushide ? 1 : 0
      });
        
      const data = response.data;  // ✅ FIXED: Changed from response.json()
      
      if (data.success) {
        if (!editMode && data.data && data.data.acgroupcode) {
          alert(`Account group created successfully!\nGroup Code: ${data.data.acgroupcode}`);
        } else {
          alert(editMode ? 'Account group updated successfully!' : 'Account group created successfully!');
        }
        setShowModal(false);
        resetForm();
        fetchGroups();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving account group:', error);
      // ✅ FIXED: Added 401 handling
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving account group. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (group) => {
    setFormData({
      acgroupid: group.acgroupid,
      companyid: group.companyid,
      acgroupcode: group.acgroupcode,
      acgroupname: group.acgroupname,
      actype: group.actype,
      acclass: group.acclass || '',
      parentid: group.parentid || '',
      grouporder: group.grouporder || 0,
      statushide: group.statushide === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account group?')) {
      return;
    }
    try {
      const response = await apiClient.delete('/Acgroups.php', {
        data: { acgroupid: id }
      });
     
      const data = response.data;
      
      if (data.success) {
        alert('Account group deleted successfully!');
        fetchGroups();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting account group:', error);
      // ✅ FIXED: Added 401 handling
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting account group. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      acgroupid: null,
      companyid: companyId || null,
      acgroupcode: '',
      acgroupname: '',
      actype: 'Asset',
      acclass: '',
      parentid: '',
      grouporder: 0,
      statushide: false
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Check session context - AFTER all hooks are called
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Account Groups (Chart of Accounts)</h2>
          <p className="subtitle">Manage hierarchical account structure</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Account Group
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by group name, code, or class..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            {accountTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-info">
          Total: {filteredGroups.length} {filteredGroups.length === 1 ? 'group' : 'groups'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading account groups...</div>
      ) : treeData.length === 0 ? (
        <div className="empty-state">
          <p>No account groups found</p>
          <button onClick={openAddModal}>Create First Group</button>
        </div>
      ) : (
        <div style={{ padding: '20px', background: '#fff', borderRadius: '8px' }}>
          <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FolderTree size={20} color="#667eea" />
            Hierarchical Chart of Accounts
          </h3>
          {renderTree(treeData)}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Account Group' : 'Add New Account Group'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {!editMode && (
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>ℹ️</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                    Group code will be auto-generated (e.g., AG001, AG002, etc.)
                  </span>
                </div>
              )}
              
              {editMode && formData.acgroupcode && (
                <div className="form-group">
                  <label>Group Code (Auto-generated)</label>
                  <input
                    type="text"
                    value={formData.acgroupcode}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="acgroupname">
                    Group Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="acgroupname"
                    value={formData.acgroupname}
                    onChange={(e) => setFormData({ ...formData, acgroupname: e.target.value })}
                    maxLength="100"
                    placeholder="e.g., Current Assets, Fixed Assets"
                    className={errors.acgroupname ? 'error' : ''}
                  />
                  {errors.acgroupname && (
                    <span className="error-message">{errors.acgroupname}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="actype">
                    Account Type <span className="required">*</span>
                  </label>
                  <select
                    id="actype"
                    value={formData.actype}
                    onChange={(e) => setFormData({ ...formData, actype: e.target.value, acclass: '' })}
                    className={errors.actype ? 'error' : ''}
                  >
                    <option value="">-- Select Type --</option>
                    {accountTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.actype && (
                    <span className="error-message">{errors.actype}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="acclass">Account Class</label>
                  <select
                    id="acclass"
                    value={formData.acclass}
                    onChange={(e) => setFormData({ ...formData, acclass: e.target.value })}
                    disabled={!formData.actype}
                  >
                    <option value="">-- Select Class --</option>
                    {formData.actype && accountClasses[formData.actype]?.map((cls) => (
                      <option key={cls} value={cls}>{cls}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="parentid">Parent Group</label>
                  <select
                    id="parentid"
                    value={formData.parentid}
                    onChange={(e) => setFormData({ ...formData, parentid: e.target.value })}
                  >
                    <option value="">-- None (Top Level) --</option>
                    {groups
                      .filter(g => g.acgroupid !== formData.acgroupid && g.actype === formData.actype)
                      .map((group) => (
                      <option key={group.acgroupid} value={group.acgroupid}>
                        {group.acgroupname} ({group.acgroupcode})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="grouporder">Display Order</label>
                  <input
                    type="number"
                    id="grouporder"
                    value={formData.grouporder}
                    onChange={(e) => setFormData({ ...formData, grouporder: e.target.value })}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.statushide}
                      onChange={(e) => setFormData({ ...formData, statushide: e.target.checked })}
                    />
                    <span>Hide/Inactive</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountGroups;